import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, MailWarning, MailQuestion } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/common/Seo";

/**
 * WO 800 §1 — /verify-email. Every verification e-mail links here with
 * ?token=<jwt>. Token is read from the query string ONLY, posted once to
 * POST /auth/verify-email, then stripped from the URL so it never sits in
 * browser history. noindex; excluded from the sitemap.
 */
export default function VerifyEmailPage() {
  const { user, token: authToken } = useAuth();
  const [state, setState] = useState("loading"); // loading | success | error | needs_token
  const [errorMsg, setErrorMsg] = useState("");
  const [resendMsg, setResendMsg] = useState("");
  const [resendWait, setResendWait] = useState(0); // seconds until retry allowed
  const [resending, setResending] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const qs = new URLSearchParams(window.location.search);
    const token = qs.get("token");
    // Strip the token from the address bar / history immediately.
    window.history.replaceState(null, "", "/verify-email");
    if (!token) {
      setState("needs_token");
      return;
    }
    sharedApi
      .post("/auth/verify-email", { token })
      .then(async () => {
        setState("success");
        // Signed-in users: refresh /auth/me so the amber banner on
        // /account reflects email_verified without a hard reload.
        if (localStorage.getItem("allsale_token")) {
          try {
            const { data } = await sharedApi.get("/auth/me");
            if (data) {
              const prev = JSON.parse(localStorage.getItem("allsale_user") || "{}");
              localStorage.setItem("allsale_user", JSON.stringify({ ...prev, ...data }));
            }
          } catch {
            /* non-fatal — /account refetches on mount anyway */
          }
        }
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        setErrorMsg(typeof detail === "string" ? detail : "Verification link is invalid or expired");
        setState("error");
      });
  }, []);

  // Countdown for the 429 Retry-After window.
  useEffect(() => {
    if (resendWait <= 0) return undefined;
    const t = setInterval(() => setResendWait((s) => (s > 1 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [resendWait > 0]);

  const signedIn = !!(authToken || localStorage.getItem("allsale_token"));

  const waitLabel = () => {
    const m = Math.floor(resendWait / 60);
    const s = resendWait % 60;
    return m > 0 ? `Try again in ${m}:${String(s).padStart(2, "0")} min` : `Try again in ${s}s`;
  };

  const resend = async () => {
    setResending(true);
    setResendMsg("");
    try {
      await sharedApi.post("/auth/verify-email/request", {});
      setResendMsg("Verification e-mail sent — check your inbox.");
    } catch (err) {
      const r = err?.response;
      const detail = typeof r?.data?.detail === "string" ? r.data.detail : "Couldn't resend right now.";
      setResendMsg(detail);
      if (r?.status === 429) {
        // Cloudflare currently strips Retry-After on /api/* (Addendum 3
        // gate #3) — default to the backend's full 15-min window when the
        // header is absent.
        const retryAfter = parseInt(r.headers?.["retry-after"], 10);
        setResendWait(Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 900);
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-4 md:px-8 py-20 text-center">
      <Seo title="Verify your email" noindex />
      <div data-testid="verify-email-status" data-state={state}>
        {state === "loading" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-[color:var(--brand-blue)] animate-spin" />
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Confirming your e-mail…</h1>
            <p className="mt-2 text-slate-500">One moment while we check your verification link.</p>
          </>
        )}

        {state === "success" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Your e-mail is confirmed</h1>
            <p className="mt-2 text-slate-500">Thanks — your Allsale account is fully verified.</p>
            <Link
              to="/account"
              data-testid="verify-email-go-account"
              className="mt-8 inline-block rounded-full bg-[color:var(--brand-blue)] text-white font-bold px-6 py-3"
            >
              Go to my account
            </Link>
          </>
        )}

        {state === "error" && (
          <>
            <MailWarning className="w-12 h-12 mx-auto text-amber-500" />
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Couldn&apos;t verify</h1>
            <p className="mt-2 text-slate-600" data-testid="verify-email-error-detail">{errorMsg}</p>
            <div className="mt-8">
              {signedIn ? (
                <>
                  <button
                    type="button"
                    data-testid="verify-email-resend"
                    onClick={resend}
                    disabled={resending || resendWait > 0}
                    className="rounded-full bg-[color:var(--brand-blue)] text-white font-bold px-6 py-3 disabled:opacity-50"
                  >
                    {resendWait > 0 ? waitLabel() : resending ? "Sending…" : "Resend verification e-mail"}
                  </button>
                  {resendMsg && <p className="mt-3 text-sm text-slate-500" data-testid="verify-email-resend-msg">{resendMsg}</p>}
                </>
              ) : (
                <p className="text-slate-500">
                  <Link to="/login" className="font-semibold text-[color:var(--brand-blue)] underline">Sign in</Link>
                  {" "}to request a fresh verification e-mail.
                </p>
              )}
            </div>
          </>
        )}

        {state === "needs_token" && (
          <>
            <MailQuestion className="w-12 h-12 mx-auto text-slate-400" />
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Verification link needed</h1>
            <p className="mt-2 text-slate-500">
              Open the verification link from your e-mail to confirm your account.
              {signedIn ? " You can also request a fresh one below." : ""}
            </p>
            <div className="mt-8">
              {signedIn ? (
                <>
                  <button
                    type="button"
                    data-testid="verify-email-resend"
                    onClick={resend}
                    disabled={resending || resendWait > 0}
                    className="rounded-full bg-[color:var(--brand-blue)] text-white font-bold px-6 py-3 disabled:opacity-50"
                  >
                    {resendWait > 0 ? waitLabel() : resending ? "Sending…" : "Send verification e-mail"}
                  </button>
                  {resendMsg && <p className="mt-3 text-sm text-slate-500" data-testid="verify-email-resend-msg">{resendMsg}</p>}
                </>
              ) : (
                <Link to="/login" className="font-semibold text-[color:var(--brand-blue)] underline">Sign in</Link>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
